// src/components/admin/page-management/section-forms/SlideshowLinearForm.js
import React, { useState } from 'react';
import ImageUploader from '../ImageUploader';
import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';

const SlideshowLinearForm = ({ content, updateContent, pageSlug }) => {
  const [slides, setSlides] = useState(content?.slides || [
    {
      imageUrl: '',
      title: '',
      subtitle: ''
    }
  ]);

  // Update slides in both local state and parent component
  const updateSlides = (newSlides) => {
    setSlides(newSlides);
    updateContent('slides', newSlides);
  };

  // Handle image upload for a specific slide
  const handleImageUpload = (imagePath, index) => {
    const newSlides = [...slides];
    newSlides[index].imageUrl = imagePath;
    updateSlides(newSlides);
  };

  // Update slide text content
  const updateSlideContent = (index, field, value) => {
    const newSlides = [...slides];
    newSlides[index][field] = value;
    updateSlides(newSlides);
  };

  // Add a new slide
  const addSlide = () => {
    updateSlides([...slides, { imageUrl: '', title: '', subtitle: '' }]);
  };

  // Remove a slide
  const removeSlide = (index) => {
    if (slides.length > 1) {
      const newSlides = slides.filter((_, i) => i !== index);
      updateSlides(newSlides);
    }
  };

  // Move slide up in order
  const moveSlideUp = (index) => {
    if (index === 0) return;
    const newSlides = [...slides];
    [newSlides[index - 1], newSlides[index]] = [newSlides[index], newSlides[index - 1]];
    updateSlides(newSlides);
  };

  // Move slide down in order
  const moveSlideDown = (index) => {
    if (index === slides.length - 1) return;
    const newSlides = [...slides];
    [newSlides[index], newSlides[index + 1]] = [newSlides[index + 1], newSlides[index]];
    updateSlides(newSlides);
  };

  return (
    <div>
      <h3 className="font-bold text-lg mb-4">Slideshow Section</h3>
      
      {/* Slideshow Settings */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-base mb-3">Slideshow Settings</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Height</label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              value={content?.height || '600px'}
              onChange={(e) => updateContent('height', e.target.value)}
              placeholder="e.g., 600px, 50vh, etc."
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Overlay Opacity (%)</label>
            <input
              type="number"
              className="w-full p-2 border rounded"
              value={content?.overlayOpacity || 40}
              onChange={(e) => updateContent('overlayOpacity', parseInt(e.target.value, 10))}
              min="0"
              max="100"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Text Color</label>
            <input
              type="color"
              className="w-full p-1 border rounded h-10"
              value={content?.textColor || '#ffffff'}
              onChange={(e) => updateContent('textColor', e.target.value)}
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Transition Speed (ms)</label>
            <input
              type="number"
              className="w-full p-2 border rounded"
              value={content?.transitionSpeed || 5000}
              onChange={(e) => updateContent('transitionSpeed', parseInt(e.target.value, 10))}
              min="1000"
              step="500"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Animation Style</label>
            <select
              className="w-full p-2 border rounded"
              value={content?.animationStyle || 'fade'}
              onChange={(e) => updateContent('animationStyle', e.target.value)}
            >
              <option value="fade">Fade</option>
              <option value="slide">Slide</option>
              <option value="zoom">Zoom</option>
            </select>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Text Alignment</label>
            <select
              className="w-full p-2 border rounded"
              value={content?.textAlignment || 'center'}
              onChange={(e) => updateContent('textAlignment', e.target.value)}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-4 mt-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="showArrows"
              className="mr-2"
              checked={content?.showArrows !== false}
              onChange={(e) => updateContent('showArrows', e.target.checked)}
            />
            <label htmlFor="showArrows" className="text-sm font-medium">Show Navigation Arrows</label>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="showIndicators"
              className="mr-2"
              checked={content?.showIndicators !== false}
              onChange={(e) => updateContent('showIndicators', e.target.checked)}
            />
            <label htmlFor="showIndicators" className="text-sm font-medium">Show Slide Indicators</label>
          </div>
        </div>
      </div>
      
      {/* Slides Management */}
      <div className="mb-4">
        <h4 className="font-medium text-base mb-3">Slides</h4>
        
        {slides.map((slide, index) => (
          <div key={index} className="border rounded-lg p-4 mb-4 bg-white">
            <div className="flex justify-between items-center mb-3">
              <h5 className="font-medium">Slide {index + 1}</h5>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => moveSlideUp(index)}
                  disabled={index === 0}
                  className={`p-1 rounded ${index === 0 ? 'text-gray-400' : 'text-blue-600 hover:bg-blue-50'}`}
                  title="Move Up"
                >
                  <ArrowUp size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => moveSlideDown(index)}
                  disabled={index === slides.length - 1}
                  className={`p-1 rounded ${index === slides.length - 1 ? 'text-gray-400' : 'text-blue-600 hover:bg-blue-50'}`}
                  title="Move Down"
                >
                  <ArrowDown size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => removeSlide(index)}
                  disabled={slides.length <= 1}
                  className={`p-1 rounded ${slides.length <= 1 ? 'text-gray-400' : 'text-red-600 hover:bg-red-50'}`}
                  title="Remove Slide"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Image URL</label>
              <input
                type="text"
                className="w-full p-2 border rounded mb-2"
                value={slide.imageUrl || ''}
                onChange={(e) => updateSlideContent(index, 'imageUrl', e.target.value)}
              />
              <ImageUploader
                onImageUpload={(imagePath) => handleImageUpload(imagePath, index)}
                pageSlug={pageSlug}
              />
            </div>
            
            {slide.imageUrl && (
              <div className="mt-2 mb-4 p-2 border rounded">
                <p className="text-sm font-medium mb-2">Preview:</p>
                <img 
                  src={slide.imageUrl} 
                  alt={`Slide ${index + 1} Preview`} 
                  className="max-w-full h-auto max-h-32 object-cover"
                />
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                value={slide.title || ''}
                onChange={(e) => updateSlideContent(index, 'title', e.target.value)}
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Subtitle</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                value={slide.subtitle || ''}
                onChange={(e) => updateSlideContent(index, 'subtitle', e.target.value)}
              />
            </div>
          </div>
        ))}
        
        <button
          type="button"
          onClick={addSlide}
          className="flex items-center justify-center w-full p-2 border border-dashed border-blue-500 rounded-lg text-blue-600 hover:bg-blue-50"
        >
          <Plus size={16} className="mr-2" /> Add Slide
        </button>
      </div>
    </div>
  );
};

export default SlideshowLinearForm;